import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import BottomNav from '../components/ui/BottomNav'
import Panel from '../components/ui/Panel'
import Chip from '../components/ui/Chip'
import Avatar from '../components/ui/Avatar'
import { buttonClasses } from '../components/ui/Button'
import texts from '../texts'
import { getClub, getClubs, getGames } from '../lib/api'
import type { Club, ClubSeasonEntry, GameSummary } from '../lib/api'
import { getMyStats } from '../lib/api.stats'
import type { MyStats } from '../lib/api.stats'
import { useOnline } from '../lib/online'

const SECTION_LABEL = 'font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2'

interface SeasonClub {
  id: number
  name: string
  season: ClubSeasonEntry[]
}

function fmt(n: number): string {
  return (n > 0 ? '+' : '') + n
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
}

function chipVariant(n: number): 'mint' | 'red' | 'neutral' {
  return n > 0 ? 'mint' : n < 0 ? 'red' : 'neutral'
}

function ActiveGameCard({ game }: { game: GameSummary }) {
  const allZero = game.players.every((p) => p.totalScore === 0)
  const leader = [...game.players].sort((a, b) => b.totalScore - a.totalScore)[0]
  const statusLine = allZero ? texts.home.noRoundsYet : texts.home.leading(leader.name, fmt(leader.totalScore))
  return (
    <div className="grid gap-1.5 rounded-2xl border border-text-2/20 bg-text-2/10 p-3.5">
      <span className="font-mono text-[0.7rem] uppercase tracking-[0.06em] text-gold">{game.clubName}</span>
      <b className="font-display text-[1.05rem]">{game.name}</b>
      <span className="text-[0.85em] text-text-2">{statusLine}</span>
      <Link to={`/game/${game.id}`} className={buttonClasses({ full: true, size: 'sm' })}>
        {texts.home.continue}
      </Link>
    </div>
  )
}

function SeasonRow({ entry, rank }: { entry: ClubSeasonEntry; rank: number }) {
  return (
    <div className="grid grid-cols-[22px_auto_1fr_auto] items-center gap-2.5">
      <span className="font-mono text-text-2">{rank}</span>
      <Avatar name={entry.name} index={rank - 1} />
      <span className="min-w-0 truncate">{entry.name}</span>
      <span
        className={`font-mono font-semibold ${entry.points > 0 ? 'text-mint' : entry.points < 0 ? 'text-red' : ''}`}
      >
        {fmt(entry.points)}
      </span>
    </div>
  )
}

function Home() {
  const [userName, setUserName] = useState('')
  const [games, setGames] = useState<GameSummary[]>([])
  const [clubs, setClubs] = useState<Club[]>([])
  const [seasonClubs, setSeasonClubs] = useState<SeasonClub[]>([])
  const [myStats, setMyStats] = useState<MyStats | null>(null)
  const [loading, setLoading] = useState(true)
  const online = useOnline()

  useEffect(() => {
    const loadAll = () => {
      getGames()
        .then((response) => {
          setUserName(response.userName)
          setGames(response.games)
        })
        .finally(() => setLoading(false))
      getClubs().then(setClubs)
      getMyStats()
        .then(setMyStats)
        .catch(() => setMyStats(null))
    }
    loadAll()

    const onVisible = () => {
      if (document.visibilityState === 'visible') loadAll()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', loadAll)

    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', loadAll)
    }
  }, [])

  // Klubberne til sæson-sektionen, ordnet efter klubbens seneste spil (klubber uden spil sidst)
  useEffect(() => {
    if (clubs.length === 0) {
      setSeasonClubs([])
      return
    }
    let cancelled = false
    const latestByClub = new Map<number, number>()
    for (const g of games) {
      if (g.clubId == null) continue
      const t = new Date(g.createdAt).getTime()
      const current = latestByClub.get(g.clubId)
      if (current === undefined || t > current) latestByClub.set(g.clubId, t)
    }
    const ordered = [...clubs].sort((a, b) => {
      const at = latestByClub.get(a.id)
      const bt = latestByClub.get(b.id)
      if (at === undefined && bt === undefined) return 0
      if (at === undefined) return 1
      if (bt === undefined) return -1
      return bt - at
    })
    Promise.all(
      ordered.slice(0, 3).map((c) =>
        getClub(c.id)
          .then((d): SeasonClub => ({ id: c.id, name: c.name, season: d.season }))
          .catch(() => null)
      )
    ).then((results) => {
      if (!cancelled) setSeasonClubs(results.filter((r): r is SeasonClub => r !== null))
    })
    return () => {
      cancelled = true
    }
  }, [games, clubs])

  const currentUserId = parseInt(localStorage.getItem('userId') || '0', 10)
  const currentYear = new Date().getFullYear()

  const clubGames = games.filter((g) => g.clubId != null)
  const activeClubGames = clubGames.filter((g) => g.status === 'active')
  const finishedClubGames = clubGames.filter((g) => g.status !== 'active')

  if (loading) {
    return (
      <Screen>
        <p className="text-text-2">…</p>
      </Screen>
    )
  }

  return (
    <Screen>
      <div className="flex items-center justify-between">
        <h1>{texts.home.greeting(userName)}</h1>
        <Link to="/me">
          <Avatar name={userName} />
        </Link>
      </div>
      {!online && <Chip variant="gold">{texts.pwa.offlineChip}</Chip>}

      <Link to="/game/new" className={buttonClasses({ full: true })}>
        {texts.home.newGame}
      </Link>

      {activeClubGames.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <span className={SECTION_LABEL}>{texts.home.inProgress}</span>
            {activeClubGames.length > 3 && (
              <Link to="/spil" className="text-[0.85em] text-gold">
                {texts.home.seeAll(activeClubGames.length)}
              </Link>
            )}
          </div>
          <div className="grid gap-2.5">
            {activeClubGames.slice(0, 3).map((game) => (
              <ActiveGameCard key={game.id} game={game} />
            ))}
          </div>
        </>
      )}

      {finishedClubGames.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <span className={SECTION_LABEL}>{texts.home.latest}</span>
            <Link to="/spil" className="text-[0.85em] text-gold">
              {texts.home.allGamesLink}
            </Link>
          </div>
          <div className="grid gap-2.5">
            {finishedClubGames.slice(0, 4).map((game) => {
              const me = game.players.find((p) => p.userId === currentUserId)
              const myScore = me?.totalScore ?? 0
              const winner = [...game.players].sort((a, b) => b.totalScore - a.totalScore)[0]
              return (
                <Link
                  key={game.id}
                  to={`/game/${game.id}`}
                  className="flex w-full items-center justify-between gap-2.5 rounded-xl border border-text-2/20 bg-text-2/10 px-3 py-2.5 text-inherit no-underline"
                >
                  <div className="min-w-0 flex-1">
                    <b className="block">{game.name}</b>
                    <span className="block text-[0.8em] text-text-2">
                      {`${game.clubName ?? ''} · ${formatDate(game.createdAt)} · ${winner ? texts.games.winner(winner.name) : ''}`}
                    </span>
                  </div>
                  <Chip variant={chipVariant(myScore)}>{texts.home.yourPoints(fmt(myScore))}</Chip>
                </Link>
              )
            })}
          </div>
        </>
      )}

      {clubs.length === 0 ? (
        <Panel>
          <b>{texts.home.noClubTitle}</b>
          <p className="text-[0.85em] text-text-2">{texts.home.noClubHint}</p>
          <Link to="/klubber" className={buttonClasses({ variant: 'ghost' })}>
            {texts.clubs.title}
          </Link>
        </Panel>
      ) : (
        seasonClubs.length > 0 && (
          <>
            <span className={SECTION_LABEL}>{texts.home.seasonHeading(currentYear)}</span>
            {seasonClubs.map((entry) => {
              const sorted = [...entry.season].sort((a, b) => b.points - a.points)
              const top3 = sorted.slice(0, 3)
              const ownIndex = sorted.findIndex((s) => s.userId === currentUserId)
              return (
                <Panel key={entry.id}>
                  <div className="flex items-center justify-between">
                    <b>{entry.name}</b>
                    <Link to={`/klub/${entry.id}`} className="text-[0.85em] text-gold">
                      {texts.home.clubLink}
                    </Link>
                  </div>
                  <div className="grid gap-2">
                    {top3.map((s, i) => (
                      <SeasonRow key={s.userId} entry={s} rank={i + 1} />
                    ))}
                    {ownIndex >= 3 && (
                      <div className="mt-1 border-t border-text-2/10 pt-1.5">
                        <SeasonRow entry={sorted[ownIndex]} rank={ownIndex + 1} />
                      </div>
                    )}
                  </div>
                </Panel>
              )
            })}
          </>
        )
      )}

      {myStats && myStats.games > 0 && (
        <>
          <span className={SECTION_LABEL}>{texts.home.yearHeading}</span>
          <Link
            to="/stats"
            className="rounded-2xl border border-text-2/20 bg-text-2/10 p-3.5 text-inherit no-underline"
          >
            {texts.home.yearSummary(
              currentYear,
              myStats.games,
              fmt(myStats.totalPoints),
              `${Math.round(myStats.asBidder.winRate * 100)} %`
            )}
          </Link>
        </>
      )}

      <BottomNav />
    </Screen>
  )
}

export default Home
